import {Injectable} from "@angular/core";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {PlayerActivity} from "../entities/players/PlayerActivity";
import {CookieService} from "ngx-cookie-service";
import {SimplePlayer} from "../entities/players/SimplePlayer";
import {BasicEventPlayer} from "../entities/auth/BasicEventPlayer";
import {CosmeticsPlayer} from "../entities/players/CosmeticsPlayer";
import {BasicResponse} from "../entities/BasicResponse";
import {BoothHistory, BoothPurchaseState, EventBooth} from "../entities/booths/EventBooth";
import {Autograph} from "../entities/players/Autograph";
import {PlayerBoothActivity} from "../entities/players/PlayerBoothActivity";
import {ScheduledAnnouncement} from "../entities/server/ScheduledAnnouncement";
import {EventRank} from "../entities/auth/EventRank";
import {Server} from "../entities/server/Server";
import {Punishment} from "../entities/players/Punishment";
import {RedisPlayer} from "../entities/players/RedisPlayer";
import {IpInfo} from "../entities/IpInfo";
import {ScheduleSlot} from "../entities/server/ScheduleSlot";
import {ScheduleItem} from "../pages/schedule/ScheduleItem";
import {BaseResponse} from "../entities/BaseResponse";
import {BoothInfo} from "../entities/booths/BoothInfo";
import {EventPlayerYear} from "../entities/players/EventPlayerYear";
import {Role} from "../entities/auth/Role";
import {QuizQuestion} from "../entities/quiz/QuizQuestion";

export interface UploadedMapImage {
  id: number;
  name: string;
  creator: string;
  asset: string;
  createdAt: number;
}

@Injectable()
export class APIService {
  baseUrl: string = "https://api.cubedcon.com/v2";

  private roles: Role[] = []
  // Optional Bearer token for specific protected endpoints (e.g., coins adjust)
  private bearerToken?: string;

  // Best-effort sync of bearer token from runtime sources to avoid race with async env loader
  private syncBearerFromRuntime(): void {
    try {
      const w: any = (typeof window !== 'undefined') ? window : undefined;
      const fromWindowEnv: string | undefined = w?.ENV?.API_BEARER_TOKEN || w?.API_BEARER_TOKEN;

      let fromMeta: string | undefined;
      if (typeof document !== 'undefined') {
        const meta = document.querySelector('meta[name="api-bearer-token"]') as HTMLMetaElement | null;
        fromMeta = meta?.content?.trim() || undefined;
      }

      const fromSession = (typeof sessionStorage !== 'undefined')
        ? (sessionStorage.getItem('api_bearer_token') || undefined)
        : undefined;

      const chosen = (fromWindowEnv && String(fromWindowEnv).trim())
        || (fromMeta && String(fromMeta).trim())
        || (fromSession && String(fromSession).trim())
        || undefined;

      if (chosen && chosen !== this.bearerToken) {
        this.bearerToken = chosen;
        try { sessionStorage.setItem('api_bearer_token', chosen); } catch {}
      }
    } catch {}
  }

  // Build Http options with Authorization header when bearer token is present
  private httpOptions(): { headers?: HttpHeaders } {
    // Ensure we pick up a token that might have been injected after app bootstrap
    this.syncBearerFromRuntime();
    if (this.bearerToken && String(this.bearerToken).trim().length > 0) {
      return { headers: new HttpHeaders({ Authorization: `Bearer ${this.bearerToken}` }) };
    }
    return {};
  }

  constructor(private httpClient: HttpClient,
              private cookies: CookieService) {
    this.getRoles().then((r) => this.roles = r);
    // Initialize bearer token from environment or session storage
    try {
      // 1) Runtime-provided global window env (e.g., injected at deploy time)
      const w: any = (typeof window !== 'undefined') ? window : undefined;
      const fromWindowEnv: string | undefined = w?.ENV?.API_BEARER_TOKEN || w?.API_BEARER_TOKEN;

      // 2) <meta name="api-bearer-token" content="..."> in index.html
      let fromMeta: string | undefined;
      if (typeof document !== 'undefined') {
        const meta = document.querySelector('meta[name="api-bearer-token"]') as HTMLMetaElement | null;
        fromMeta = meta?.content?.trim() || undefined;
      }

      // 3) Fallback to sessionStorage (persisted from previous UI entry)
      const fromSession = sessionStorage.getItem('api_bearer_token') || undefined;

      const chosen = (fromWindowEnv && String(fromWindowEnv).trim())
        || (fromMeta && String(fromMeta).trim())
        || (fromSession && String(fromSession).trim())
        || undefined;

      if (chosen) {
        this.bearerToken = chosen;
        // keep in session for subsequent navigations within the same tab
        sessionStorage.setItem('api_bearer_token', chosen);
      }
    } catch {}
  }

  async listRoles(): Promise<Role[]> {
    if (this.roles.length > 0)
      return this.roles;
    return this.getRoles();
  }

  hasEntitlement(entitlements: string[], entitlement: string): boolean {
    if (entitlements.indexOf('PRODUCTION') !== -1) {
      return true;
    }

    if (entitlement.toUpperCase() === 'OPERATIONS') {
      return entitlements.indexOf('OPERATIONS') !== -1 ||
        this.hasEntitlement(entitlements, 'PRODUCTION');
    }

    if (entitlement.toUpperCase() === 'MODERATION') {
      return entitlements.indexOf('MODERATION') !== -1 ||
        this.hasEntitlement(entitlements, 'OPERATIONS');
    }

    if (entitlement.toUpperCase() === 'STAFF') {
      return entitlements.indexOf('STAFF') !== -1 ||
        this.hasEntitlement(entitlements, 'MODERATION');
    }
    return false;
  }

  getToken(): string {
    return this.cookies.get('token');
  }

  get<T>(url: string): Promise<T> {
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.get<BaseResponse<T>>(this.baseUrl + url, opts).subscribe({
        next: data => {
          if (data.ok) {
            resolve(data.response!);
          } else {
            reject(data.message);
          }
        },
        error: err => {
          reject(err);
        }
      })
    });
  }

  post<T, U>(url: string, body: U): Promise<T | undefined> {
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.post<BaseResponse<T>>(this.baseUrl + url, body, opts).subscribe({
        next: data => {
          if (data.ok) {
            if (data.response === undefined) {
              resolve(undefined);
            } else {
              resolve(data.response!);
            }
          } else {
            reject(data.message);
          }
        },
        error: err => {
          reject(err);
        }
      })
    });
  }

  put<T, U>(url: string, body: U): Promise<T | undefined> {
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.put<BaseResponse<T>>(this.baseUrl + url, body, opts).subscribe({
        next: data => {
          if (data.ok) {
            if (data.response === undefined) {
              resolve(undefined);
            } else {
              resolve(data.response!);
            }
          } else {
            reject(data.message);
          }
        },
        error: err => {
          reject(err);
        }
      })
    });
  }

  del<T>(url: string): Promise<T | undefined> {
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.delete<BaseResponse<T>>(this.baseUrl + url, opts).subscribe({
        next: data => {
          if (data.ok) {
            if (data.response === undefined) {
              resolve(undefined);
            } else {
              resolve(data.response!);
            }
          } else {
            reject(data.message);
          }
        },
        error: err => reject(err)
      });
    });
  }

  async getUploadedMapImages(): Promise<UploadedMapImage[]> {
    return this.get<UploadedMapImage[]>('/asset/images/me');
  }

  uploadMapImage(file: File): Promise<UploadedMapImage> {
    return new Promise((resolve, reject) => {
      const form = new FormData();
      form.append('image', file, file.name);
      const opts = this.httpOptions();
      this.httpClient.post<BaseResponse<UploadedMapImage>>(this.baseUrl + '/asset/image', form, opts).subscribe({
        next: data => data.ok && data.response ? resolve(data.response) : reject(data.message || 'Image upload failed'),
        error: err => reject(err)
      });
    });
  }

  async deleteUploadedMapImage(id: number): Promise<void> {
    await this.del<void>(`/asset/images/${id}`);
  }

  // Booth owner change requests
  async listOwnerChangeRequests(status: 'PENDING' | 'ACCEPTED' | 'DENIED' | 'ALL' = 'PENDING') {
    const q = status ? (`?status=${status}`) : '';
    return this.get<any[]>(`/booths/owner-change/requests${q}`);
  }

  async acceptOwnerChangeRequest(requestId: number) {
    return this.post<void, {}>(`/booths/owner-change/${requestId}/accept`, {});
  }

  async denyOwnerChangeRequest(requestId: number, reason: string) {
    return this.post<void, {reason: string}>(`/booths/owner-change/${requestId}/deny`, { reason });
  }

  searchPlayers(text: string): Promise<{ players: SimplePlayer[] } | undefined> {
    return this.post<{ players: SimplePlayer[] }, { username: string }>("/players/search", {username: text});
  }

  create(name: string): Promise<BasicEventPlayer | undefined> {
    return this.post("/players", {username: name});
  }

  getIpInfo(ip: string): Promise<IpInfo> {
    return new Promise((resolve, reject) => {
      this.httpClient.get<IpInfo>("https://freeipapi.com/api/json/" + ip).subscribe({
        next: data => {
          resolve(data);
        }
      })
    });
  }

  check2fa(): Promise<string> {
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.get<BasicResponse>(this.baseUrl + "/auth/2fa/check", opts).subscribe({
        next: data => {
          resolve(data.success ? "" : data.error);
        }
      })
    });
  }

  allow2fa(): Promise<BasicResponse> {
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.post<BasicResponse>(this.baseUrl + "/auth/2fa/complete", {}, opts).subscribe({
        next: data => {
          resolve(data);
        }
      })
    });
  }

  reject2fa(): Promise<BasicResponse> {
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.post<BasicResponse>(this.baseUrl + "/auth/2fa/reject", {}, opts).subscribe({
        next: data => {
          resolve(data);
        }
      })
    });
  }

  getEntitlements(): Promise<string[]> {
    return this.get("/ranks/entitlements");
  }

  getRoles(): Promise<Role[]> {
    return this.get<any>("/ranks/roles").then((r) => {
      let raw: any[] = [];

      if (Array.isArray(r)) {
        raw = r as any[];
      } else if (r && Array.isArray(r.role)) {
        raw = r.role as any[];
      } else if (r && r.role && typeof r.role === 'object') {
        // handle map/object of roles keyed by id/name
        raw = Object.values(r.role) as any[];
      } else if (r && typeof r === 'object') {
        // fallback: if r is an object where values are roles
        const values = Object.values(r);
        // if values is single array containing roles, unwrap it
        if (values.length === 1 && Array.isArray(values[0])) {
          raw = values[0] as any[];
        } else {
          raw = values as any[];
        }
      }

      // Normalize to our Role interface with consistent number ids
      const normalized: Role[] = (raw || []).map((it: any) => ({
        id: Number(it?.id),
        name: String(it?.name ?? ''),
        edit_permission: it?.edit_permission
      }));

      return normalized;
    });
  }

  getRanks(): Promise<EventRank[]> {
    return this.get("/ranks/");
  }

  getPlayer(text: string): Promise<BasicEventPlayer> {
    return this.get("/players/" + text);
  }

  getRankPlayers(id: number): Promise<BasicEventPlayer[]> {
    return this.get<any>("/players?rank=" + id).then((r) => this.normalizePlayersArray(r));
  }

  getRolePlayers(id: number): Promise<BasicEventPlayer[]> {
    return this.get<any>("/players?role=" + id).then((r) => this.normalizePlayersArray(r));
  }

  getRoleName(id: number): string {
    // If roles not yet loaded, trigger a background refresh
    if (!this.roles || this.roles.length === 0) {
      this.getRoles().then(r => this.roles = r).catch(() => {});
    }

    const nid = Number(id);
    const match = (this.roles || []).find((r) => Number((r as any).id) === nid);
    return match?.name || "Unknown";
  }

  // Normalize various response shapes into a clean, de-duplicated BasicEventPlayer[]
  private normalizePlayersArray(input: any): BasicEventPlayer[] {
    // Unwrap common shapes
    let arr: any[] = [];
    if (Array.isArray(input)) {
      arr = input as any[];
    } else if (input && Array.isArray(input.players)) {
      arr = input.players as any[];
    } else if (input && typeof input === 'object') {
      // Map/object of players
      arr = Object.values(input) as any[];
    }

    // Filter invalid items and de-duplicate by uuid
    const byUuid = new Map<string, BasicEventPlayer>();
    for (const it of arr) {
      if (!it) continue;
      const uuid = String(it.uuid ?? '').trim();
      const username = String(it.username ?? '').trim();
      if (!uuid || !username) continue;
      // Coerce core fields
      const norm: BasicEventPlayer = {
        uuid,
        username,
        ranks: Array.isArray(it.ranks) ? it.ranks : [],
        entitlements: Array.isArray(it.entitlements) ? it.entitlements : [],
        roles: Array.isArray(it.roles) ? it.roles : []
      } as BasicEventPlayer;
      byUuid.set(uuid, Object.assign({}, byUuid.get(uuid), norm));
    }
    return Array.from(byUuid.values());
  }

  async addPlayerRole(player: BasicEventPlayer, roleId: number): Promise<BasicResponse | undefined> {
    // node-api expects roleId and starts (ms) at /players/:identifier/role
    const body = {roleId: roleId, starts: Date.now()} as { roleId: number; starts: number; ends?: number };
    return this.post<BasicResponse, typeof body>("/players/" + player.uuid + "/role", body);
  }

  async removePlayerRole(player: BasicEventPlayer, roleId: number): Promise<BasicResponse | undefined> {
    // Find the ACTIVE assignmentId for this role (ends undefined or in the future), then DELETE it
    type RoleAssignment = {
      assignmentId: number | string | bigint;
      information?: { role?: number | string | bigint; starts?: number | string | bigint; ends?: number | string | bigint };
    };
    const assignments = await this.get<RoleAssignment[]>("/players/" + player.uuid + "/roles");
    const now = Date.now();

    const candidates = (assignments || []).filter(a => {
      const roleVal = (a.information?.role as any);
      if (roleVal === undefined || roleVal === null) return false;
      const roleNum = typeof roleVal === 'bigint' ? Number(roleVal) : Number(roleVal);
      return Number.isFinite(roleNum) && roleNum === roleId;
    });

    if (candidates.length === 0) {
      // Nothing to remove, treat as success
      return undefined;
    }

    // Prefer active assignments (no ends or ends in the future)
    const active = candidates.filter(a => {
      const endsVal = (a.information?.ends as any);
      if (endsVal === undefined || endsVal === null) return true;
      const endsNum = typeof endsVal === 'bigint' ? Number(endsVal) : Number(endsVal);
      return Number.isFinite(endsNum) && endsNum > now;
    });

    const pickFrom = active.length > 0 ? active : candidates;

    // Pick the most recent by starts, fallback to highest assignmentId
    const chosen = pickFrom.reduce((best, cur) => {
      const bestStartsVal = (best?.information?.starts as any);
      const curStartsVal = (cur?.information?.starts as any);
      const bestStarts = bestStartsVal !== undefined && bestStartsVal !== null ? (typeof bestStartsVal === 'bigint' ? Number(bestStartsVal) : Number(bestStartsVal)) : -Infinity;
      const curStarts = curStartsVal !== undefined && curStartsVal !== null ? (typeof curStartsVal === 'bigint' ? Number(curStartsVal) : Number(curStartsVal)) : -Infinity;
      if (curStarts !== bestStarts) {
        return curStarts > bestStarts ? cur : best;
      }
      // tie-breaker by assignmentId
      const bestIdVal = (best?.assignmentId as any);
      const curIdVal = (cur?.assignmentId as any);
      const bestId = bestIdVal === undefined || bestIdVal === null ? -Infinity : (typeof bestIdVal === 'bigint' ? Number(bestIdVal) : Number(bestIdVal));
      const curId = curIdVal === undefined || curIdVal === null ? -Infinity : (typeof curIdVal === 'bigint' ? Number(curIdVal) : Number(curIdVal));
      return curId > bestId ? cur : best;
    }, pickFrom[0]);

    if (!chosen) {
      // Fallback: nothing sensible chosen, treat as success
      return undefined;
    }

    const idVal = chosen.assignmentId as any;
    const assignmentId = typeof idVal === 'bigint' ? (idVal as bigint).toString() : String(idVal);
    return this.del<BasicResponse>("/players/" + player.uuid + "/role/" + assignmentId);
  }

  getStatus(text: string): Promise<RedisPlayer> {
    // No REST endpoint implemented for status in node-api; return offline placeholder
    return new Promise((resolve) => {
      resolve({username: ''});
    })
  }

  getGivenAutographs(id: string): Promise<Autograph[]> {
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.get<Autograph[]>(this.baseUrl + "/player/autographs/" + id + "/given", opts).subscribe({
        next: data => {
          resolve(data);
        }
      })
    });
  }

  getBooths(): Promise<EventBooth[]> {
    return this.get("/booths/")
  }

  getBooth(id: string): Promise<EventBooth> {
    return this.get("/booths/" + id);
  }

  getBoothInfo(id: string): Promise<BoothInfo> {
    return this.get("/booths/" + id + "/info");
  }

  getBoothHistory(id: string): Promise<BoothHistory> {
    return this.get("/booths/" + id + "/history");
  }

  /** Send a specific email template for a booth (admin only). */
  sendBoothEmail(id: number | string, template: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.post<any>(this.baseUrl + "/booths/" + id + "/email/" + encodeURIComponent(template), {}, opts)
        .subscribe({
          next: _ => resolve(true),
          error: _ => reject(false)
        });
    });
  }

  /** Send a specific email template to ALL approved booths (admin only). */
  sendGlobalBoothEmail(template: string): Promise<{sent: number}> {
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.post<any>(this.baseUrl + "/booths/email/" + encodeURIComponent(template), {}, opts)
        .subscribe({
          next: data => resolve({ sent: data?.sent ?? 0 }),
          error: _ => reject({ sent: 0 })
        });
    });
  }

  changePurchaseState(id: number, state: BoothPurchaseState, comment?: string, email?: boolean): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      this.put("/booths/" + id + "/purchase", {id: id, message: comment, state: state})
        .then((r) => resolve(true))
        .catch((e) => reject(false))
    })
  }

  editBooth(id: string, name: string, type: string, owner: string): Promise<BasicResponse> {
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.put<BasicResponse>(this.baseUrl + "/booth/booth/" + id,
        {name: name, type: type, owner: owner}, opts).subscribe({
        next: data => {
          resolve(data);
        }
      })
    });
  }

  addMember(id: string, name: string): Promise<BasicResponse> {
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.post<BasicResponse>(this.baseUrl + "/booth/booth/" + id + "/member",
        {username: name}, opts).subscribe({
        next: data => {
          resolve(data);
        }
      })
    });
  }

  getPriorityRank(ranks: EventRank[]): EventRank | null {
    if (ranks.length === 0) {
      return null;
    }

    const sorted = ranks.sort((a, b) => b.priority - a.priority);
    return sorted[0];
  }

  removeMember(id: string, name: string): Promise<BasicResponse> {
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.delete<BasicResponse>(this.baseUrl + "/booth/booth/" + id + "/member",
        { body: {username: name}, ...(opts.headers ? { headers: opts.headers } : {}) },).subscribe({
        next: data => {
          resolve(data);
        }
      })
    });
  }

  updatePermissions(player: BasicEventPlayer, ranks: number[], entitlements: string[]): Promise<BasicResponse | undefined> {
    return this.put("/players/" + player.uuid + "/privileges", {ranks: ranks, entitlements: entitlements})
  }

  getEventPass(uuid: string): Promise<CosmeticsPlayer> {
    return this.get("/players/cosmetics/" + uuid + "/data")
  }

  // Get a player's current coin balance via node-api coins endpoint
  async getPlayerCoins(identifier: string): Promise<number> {
    return await new Promise<number>((resolve, reject) => {
      const url = this.baseUrl + `/players/${identifier}/coins`;
      const handle = (res: BaseResponse<{ coins: number }>) => {
        if (res?.ok && typeof res?.response?.coins === 'number') {
          resolve(res.response.coins);
        } else {
          reject(res?.message || 'Failed to fetch coins');
        }
      };

      // Pick up any token injected after bootstrap
      this.syncBearerFromRuntime();

      if (this.bearerToken) {
        const headers = new HttpHeaders({ Authorization: `Bearer ${this.bearerToken}` });
        this.httpClient.get<BaseResponse<{ coins: number }>>(url, { headers }).subscribe({
          next: handle,
          error: (err) => reject(err)
        });
      } else {
        this.httpClient.get<BaseResponse<{ coins: number }>>(url).subscribe({
          next: handle,
          error: (err) => reject(err)
        });
      }
    });
  }

  // Adjust a player's coins by a delta (positive to give, negative to take)
  async adjustPlayerCoins(uuid: string, amount: number, reason?: string, sendMessage: boolean = false): Promise<void> {
    const body = { amount, reason, sendMessage } as { amount: number; reason?: string; sendMessage?: boolean };
    // Call endpoint directly to allow optional Authorization header
    await new Promise<void>((resolve, reject) => {
      const url = this.baseUrl + `/players/${uuid}/coins/adjust`;
      const onSuccess = (data: BaseResponse<unknown>) => {
        if (data.ok) resolve(); else reject(data.message);
      };

      // Pick up any token injected after bootstrap
      this.syncBearerFromRuntime();

      if (this.bearerToken) {
        const headers = new HttpHeaders({ Authorization: `Bearer ${this.bearerToken}` });
        this.httpClient.post<BaseResponse<unknown>>(url, body, { headers }).subscribe({
          next: onSuccess,
          error: (err) => reject(err)
        });
      } else {
        this.httpClient.post<BaseResponse<unknown>>(url, body).subscribe({
          next: onSuccess,
          error: (err) => reject(err)
        });
      }
    });
  }

  async givePlayerCoins(uuid: string, amount: number, reason?: string, sendMessage: boolean = false): Promise<void> {
    if (amount <= 0) throw new Error('Amount must be positive');
    return this.adjustPlayerCoins(uuid, amount, reason, sendMessage);
  }

  async takePlayerCoins(uuid: string, amount: number, reason?: string, sendMessage: boolean = false): Promise<void> {
    if (amount <= 0) throw new Error('Amount must be positive');
    return this.adjustPlayerCoins(uuid, -amount, reason, sendMessage);
  }

  // Bearer token helpers for demonstration/usage from site-control
  setBearerToken(token?: string) {
    this.bearerToken = token?.trim() || undefined;
    try {
      if (this.bearerToken) sessionStorage.setItem('api_bearer_token', this.bearerToken);
      else sessionStorage.removeItem('api_bearer_token');
    } catch {}
  }

  getBearerToken(): string | undefined {
    return this.bearerToken;
  }

  getActivity(): Promise<PlayerActivity[]> {
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.get<PlayerActivity[]>(this.baseUrl + "/player/activity", opts).subscribe({
        next: data => {
          resolve(data);
        }
      })
    });
  }

  getBoothActivity(): Promise<PlayerBoothActivity[]> {
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.get<PlayerBoothActivity[]>(this.baseUrl + "/player/boothactivity", opts).subscribe({
        next: data => {
          resolve(data);
        }
      })
    });
  }

  getScheduledAnnouncements(): Promise<ScheduledAnnouncement[]> {
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.get<ScheduledAnnouncement[]>(this.baseUrl + "/announcement/list", opts).subscribe({
        next: data => {
          resolve(data);
        }
      })
    });
  }

  getQuizQuestions(): Promise<QuizQuestion[]> {
    // return Promise.resolve([
    //   {
    //     id: 1,
    //     questionText: "Hello World?",
    //     answers: [
    //       {
    //         id: 1,
    //         answerText: "Test",
    //         correct: true
    //       }
    //     ],
    //     shown: false,
    //     category: "Test"
    //   }
    // ]);
    return this.get("/quiz/questions");
  }

  getCurrentQuestion(): Promise<QuizQuestion> {
    // return Promise.resolve(
    //   {
    //     id: 2,
    //     questionText: "Hello World?",
    //     answers: [
    //       {
    //         id: 1,
    //         answerText: "Test",
    //         correct: false
    //       }
    //     ],
    //     shown: false,
    //     category: "Test"
    //   }
    // );
    return this.get("/quiz/current");
  }

  setCurrentQuestion(questionId: number): Promise<QuizQuestion | undefined> {
    return this.post("/quiz/current", {id: questionId});
  }

  syncQuestions() {
    return this.post("/quiz/sync", {});
  }

  getSchedule(): Promise<ScheduleSlot[]> {
    // Legacy behavior: fetch today's full schedule (includes finished for today)
    // Keep this stable to avoid breaking legacy consumers.
    return this.get<any>("/schedule/today?full=1").then((r) => {
      if (Array.isArray(r)) {
        return r as ScheduleSlot[];
      }

      // Common shapes: { slots: [...] }
      if (r && Array.isArray(r.slots)) {
        return r.slots as ScheduleSlot[];
      }

      // { slots: { id: slot, ... } }
      if (r && r.slots && typeof r.slots === 'object') {
        return Object.values(r.slots) as ScheduleSlot[];
      }

      // { response: { slots: [...] } } or { data: { slots: [...] } }
      const inner = (r && (r.response || r.data)) || undefined;
      if (inner) {
        if (Array.isArray(inner)) return inner as ScheduleSlot[];
        if (Array.isArray(inner?.slots)) return inner.slots as ScheduleSlot[];
        if (inner?.slots && typeof inner.slots === 'object') return Object.values(inner.slots) as ScheduleSlot[];
      }

      // Single-field wrapper where the only property is an array
      if (r && typeof r === 'object') {
        const values = Object.values(r);
        if (values.length === 1 && Array.isArray(values[0])) {
          return values[0] as ScheduleSlot[];
        }
      }

      return [] as ScheduleSlot[];
    });
  }

  // New: fetch ALL schedule entries across days from /schedule/all
  getScheduleAll(): Promise<ScheduleSlot[]> {
    return this.get<any>("/schedule/all").then((r) => {
      if (Array.isArray(r)) return r as ScheduleSlot[];
      if (r && Array.isArray(r.slots)) return r.slots as ScheduleSlot[];
      if (r && r.slots && typeof r.slots === 'object') return Object.values(r.slots) as ScheduleSlot[];
      const inner = (r && (r.response || r.data)) || undefined;
      if (inner) {
        if (Array.isArray(inner)) return inner as ScheduleSlot[];
        if (Array.isArray(inner?.slots)) return inner.slots as ScheduleSlot[];
        if (inner?.slots && typeof inner.slots === 'object') return Object.values(inner.slots) as ScheduleSlot[];
      }
      if (r && typeof r === 'object') {
        const values = Object.values(r);
        if (values.length === 1 && Array.isArray(values[0])) return values[0] as ScheduleSlot[];
      }
      return [] as ScheduleSlot[];
    });
  }

  getFinishedSchedule(): Promise<ScheduleSlot[]> {
    return this.get<any>("/schedule/finished").then((r) => {
      if (Array.isArray(r)) return r as ScheduleSlot[];
      if (r && Array.isArray(r.slots)) return r.slots as ScheduleSlot[];
      if (r && r.slots && typeof r.slots === 'object') return Object.values(r.slots) as ScheduleSlot[];
      const inner = (r && (r.response || r.data)) || undefined;
      if (inner) {
        if (Array.isArray(inner)) return inner as ScheduleSlot[];
        if (Array.isArray(inner?.slots)) return inner.slots as ScheduleSlot[];
        if (inner?.slots && typeof inner.slots === 'object') return Object.values(inner.slots) as ScheduleSlot[];
      }
      if (r && typeof r === 'object') {
        const values = Object.values(r);
        if (values.length === 1 && Array.isArray(values[0])) return values[0] as ScheduleSlot[];
      }
      return [] as ScheduleSlot[];
    });
  }

  updateItem(item: ScheduleItem, data: any): Promise<BasicResponse> {
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.put<BasicResponse>(this.baseUrl + "/schedule/update/" +
        item.id, data, opts).subscribe({
        next: data => {
          resolve(data);
        }
      })
    });
  }

  createItem(data: any): Promise<BasicResponse> {
    // Expects data to include: name, type, location, starts, ends, delayed (optional), cancelled (optional)
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.post<BasicResponse>(this.baseUrl + "/schedule/create", data, opts).subscribe({
        next: data => resolve(data),
        error: err => reject(err)
      })
    });
  }

  deleteItem(item: ScheduleItem): Promise<void> {
    return this.del<void>(`/schedule/${item.id}`).then(() => undefined);
  }

  getServers(): Promise<Server[]> {
    return new Promise((resolve, reject) => {
      const opts = this.httpOptions();
      this.httpClient.get<Server[]>(this.baseUrl + "/servers", opts).subscribe({
        next: data => {
          resolve(data);
        }
      })
    });
  }

  getPunishments(active: boolean): Promise<Punishment[]> {
    return new Promise((resolve, reject) => {
      this.httpClient.get<Punishment[]>(this.baseUrl + "/punishment/list" + (active ? "/active" : "")).subscribe({
        next: data => {
          resolve(data);
        }
      })
    });
  }

  quashPunishment(id: number): Promise<BasicResponse> {
    return new Promise((resolve, reject) => {
      this.httpClient.post<BasicResponse>(this.baseUrl + "/punishment/quash/" + id, {}).subscribe({
        next: data => {
          resolve(data);
        }
      })
    });
  }

  punish(player: BasicEventPlayer, type: string, reason: string, ends: number): Promise<BasicResponse> {
    return new Promise((resolve, reject) => {
      this.httpClient.post<BasicResponse>(this.baseUrl + "/punishment/punish/" + player.uuid,
        {type: type, reason: reason, ends: ends}).subscribe({
        next: data => {
          resolve(data);
        }
      })
    });
  }

  getPunishmentsForPlayer(player: BasicEventPlayer): Promise<Punishment[]> {
    return this.get("/punish/list/" + player.uuid);
  }

  getAttendance(player: BasicEventPlayer): Promise<EventPlayerYear[]> {
    return this.get("/players/" + player.uuid + "/attendance");
  }
}
